import { SessionSummary } from '@bioliberty/lifemind';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { useCallback } from 'react';
import { useGetInternalSessionsExportMutation } from '../../api/internal';
import { Button } from '../../components/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/DropdownMenu';
import { DateRangePickerInput } from '../../components/Input/DateRangePickerInput';
import { useToaster } from '../../hooks/useToaster';
import { InternalTabsList } from '../components/InternalTabsList';
import { MonthlySessionsTile } from '../components/MonthlySessionsTile';

export interface SessionsTabProps {
  sessions: SessionSummary[];
  dateRange: [Date, Date] | null;
  onDateRangeChange: (range: [Date, Date] | null) => void;
}

export function SessionsTab({ sessions, dateRange, onDateRangeChange }: SessionsTabProps) {
  const [exportSessions, { isLoading }] = useGetInternalSessionsExportMutation();
  const toast = useToaster();

  const onExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      if (isLoading) {
        return;
      }

      try {
        await exportSessions({
          format
        }).unwrap();
        toast.success('The sessions export is ready and will download shortly.');
      } catch {
        toast.error('Failed to export the sessions. Please try again.');
      }
    },
    [exportSessions, toast, isLoading]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between">
        <InternalTabsList />
        <div className="flex gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex items-center gap-1">
                <Button icon={Download} isLoading={isLoading} variant="transparent" size="sm">
                  Export All Sessions
                </Button>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => onExport('csv')}>
                <FileText className="ms-1" />
                CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport('xlsx')}>
                <FileSpreadsheet className="ms-1" />
                Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DateRangePickerInput
            value={dateRange}
            onChange={onDateRangeChange}
            className="w-[350px]"
            testId="date-range-input"
          />
        </div>
      </div>
      <MonthlySessionsTile sessions={sessions} />
    </div>
  );
}
